import React, { useRef, useState, useEffect } from "react";
import Utils from "../../../../utils";
import { toast } from "react-toastify";
import {
  getEnvelopeDocumentCheckStorage,
  modifyEnvelopeSignDocument,
  checkMyFilesEnabled
} from "../../../../services/CommonService";
import { Tabs, Tab } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import SiteFilesModal from './SiteFilesModal';
import './PdfUploadPopup.css';

function PdfUploadPopup(props) {
  const clsFormButtonRef = useRef(null);
  const fileInputRef = useRef(null);

  const [docId, setDocId] = useState(0);
  const [docName, setDocName] = useState("");
  const [docDetail, setDocDetail] = useState("");
  const [documentsList, setDocumentsList] = useState([]);
  const [fileKey, setFileKey] = useState(Date.now());
  const [removeDocIds, setRemoveDocIds] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSiteFilesModalOpen, setIsSiteFilesModalOpen] = useState(false);
  const [siteFileLinks, setSiteFileLinks] = useState([]);
  const [isMyFilesEnabled, setIsMyFilesEnabled] = useState(
    typeof props.isMyFilesEnabled === 'boolean' ? props.isMyFilesEnabled : true
  );
  const [activeTab, setActiveTab] = useState("system-files");
  const singleMode = useRef(null);
  const [mode, setMode] = useState(1);

  useEffect(() => {
    setDocId(props?.docData?.id);
    setDocName(
      props?.docData?.doc_name === undefined ? "" : props?.docData?.doc_name
    );
    setDocDetail(
      props?.docData?.doc_detail === undefined ? "" : props?.docData?.doc_detail
    );
    setDocumentsList(
      props?.docData?.documents === undefined ? [] : props?.docData?.documents
    );
    if (singleMode.current) {
      singleMode.current.checked = true;
    }
  }, [props?.docData, props.formObj]);

  useEffect(() => {
    console.log("Documents List Updated:", documentsList);
  }, [documentsList]);

  let errorsObj = {
    doc_name: "",
    doc_detail: "",
    doc_file: "",
  };
  const [errors, setErrors] = useState(errorsObj);

  function clearForm(e) {
    e.preventDefault();
  }

  useEffect(() => {
    if (typeof props.isMyFilesEnabled === 'boolean') {
      setIsMyFilesEnabled(props.isMyFilesEnabled);
    } else {
      // fallback: fetch if not provided as prop
      checkMyFilesEnabled({})
        .then(res => {
          if (res?.data?.data?.myfiles_enabled === true) {
            setIsMyFilesEnabled(true);
          } else {
            setIsMyFilesEnabled(false);
          }
        })
        .catch(() => setIsMyFilesEnabled(false));
    }
  }, [props.isMyFilesEnabled]);

  // Automatically switch to system-files tab when Docusafe My Files is hidden
  useEffect(() => {
    if (!isMyFilesEnabled && activeTab === "local-site-files") {
      setActiveTab("system-files");
    }
  }, [isMyFilesEnabled, activeTab]);

  // Automatically switch to single recipient mode when only one recipient is selected
  useEffect(() => {
    if (props.formObj?.recipient_List?.length <= 1 && mode === 2) {
      setMode(1);
    }
  }, [props.formObj?.recipient_List, mode]);

  const processFiles = (files) => {
    console.log("Processing Files:", files);
    let newFiles = [];
    let hasNonPdf = false;
    let hasOversizedFile = false;

    // Only process the first file to ensure single file selection
    if (files.length > 1) {
      toast.error("Only one file can be selected at a time.");
      return;
    }

    const file = files[0];
    console.log("File:", file);

    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    const isWithinSizeLimit = file.size <= 8 * 1024 * 1024;

    if (!isPdf) {
      hasNonPdf = true;
      toast.error(`File ${file.name} is not a PDF`);
    }

    if (!isWithinSizeLimit) {
      hasOversizedFile = true;
      toast.error(`File ${file.name} exceeds 8MB limit`);
    }

    if (!hasNonPdf && !hasOversizedFile) {
      let kb = file.size / 1024;
      newFiles.push({
        id: 0,
        name: file.name,
        kb: parseFloat(kb).toFixed(2),
        file: file,
      });
    }

    if (hasNonPdf) {
      toast.error("Only PDF files are allowed!");
    }

    if (hasOversizedFile) {
      toast.error("Maximum file size is 8MB");
    }

    if (newFiles.length > 0) {
      // Replace the documentsList with the new file
      setDocumentsList(newFiles);
      setSiteFileLinks([]); // Clear any existing site file links
      setFileKey(Date.now());
    }
  };

  const onDocFile = (e) => {
    console.log("onDocFile Triggered:", e.target.files);
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      processFiles(filesArray);
      // Reset the input to allow selecting same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      console.log("No files selected in onDocFile.");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const filesArray = Array.from(files);
      processFiles(filesArray);
    }
  };

  const onRemoveDoc = (e, obj) => {
    e.preventDefault();
    setDocumentsList((current) =>
      current.filter((doc) => doc.name !== obj.name)
    );
    if (obj.id > 0) {
      setRemoveDocIds(prev => [...prev, obj.id]);
    }
    setSiteFileLinks([]); // Clear site file links if the file is removed
  };

  const handleDownload = async (e, obj) => {
    e.preventDefault();
    props.setLoading(true);
    await Utils.downloadAnyFile(obj.path, obj.name);
    props.setLoading(false);
  };

  function clearDocumentForm() {
    setDocId(0);
    setDocName("");
    setDocDetail("");
    setDocumentsList([]);
    setMode(1);
    setRemoveDocIds([]);
    setFileKey(Date.now());
    setSiteFileLinks([]);
    setErrors({
      recipients: "",
      doc_name: "",
      doc_detail: "",
      doc_file: "",
    });
  }

  async function onSaveDoc(e) {
    e.preventDefault();
    let error = false;
    const errorObj = { ...errors };
    const envelopeFormData = { ...props.formObj };
    let totalKb = 0;

    if (!docName) {
      errorObj.doc_name = "Document name is required";
      error = true;
    }

    if (documentsList.length === 0) {
      errorObj.doc_file = "Please upload exactly one document";
      error = true;
    }

    // Show toast message for validation errors
    if (!docName && documentsList.length === 0) {
      toast.error("Please enter document name and upload one document");
    } else if (!docName) {
      toast.error("Please enter document name");
    } else if (documentsList.length === 0) {
      toast.error("Please upload one document");

      // Scroll to upload section within the offcanvas
      setTimeout(() => {
        const offcanvasBody = document.querySelector('.modern-body');
        const uploadSection = document.querySelector('.modern-upload-section');
        if (offcanvasBody && uploadSection) {
          const uploadSectionTop = uploadSection.offsetTop;
          offcanvasBody.scrollTo({
            top: uploadSectionTop - 100,
            behavior: 'smooth'
          });
        }
      }, 100);
    }

    if (documentsList.length > 1) {
      errorObj.doc_file = "Only one file can be uploaded at a time";
      error = true;
    }

    if (documentsList.length === 1) {
      totalKb = parseFloat(documentsList[0]["kb"]);
      let totalMb = totalKb / 1024;
      if (totalMb > 8) {
        toast.error("Maximum 8 MB file size allowed");
        error = true;
      }
    }

    setErrors(errorObj);
    if (error) return;

    props.setLoading(true);
    const formData = new FormData();
    formData.append("envelope_id", envelopeFormData.envelope_id);
    formData.append("mode", mode);
    formData.append("doc_id", docId);
    formData.append("name", docName);
    formData.append("detail", docDetail);

    documentsList.forEach(doc => {
      if (doc.file) {
        formData.append("doc_file", doc.file);
      }
    });

    if (removeDocIds.length > 0) {
      formData.append("remove_doc_id", JSON.stringify(removeDocIds));
    }

    if (siteFileLinks.length > 0) {
      formData.append('doc_link', JSON.stringify(siteFileLinks));
    }

    let isCall = true;

    if (totalKb > 0) {
      isCall = false;
      try {
        const res = await getEnvelopeDocumentCheckStorage({ kb: totalKb });
        if (res.data.is_storage_full === false) {
          isCall = true;
        } else {
          toast.error(res.data.message);
          props.setLoading(false);
          return;
        }
      } catch (err) {
        props.setLoading(false);
        toast.error(Utils.getErrorMessage(err));
        return;
      }
    }

    try {
      const response = await modifyEnvelopeSignDocument(formData);
      clsFormButtonRef.current?.click();
      clearDocumentForm();

      let fileName = response.data.data.path.split("/").pop();
      let obj = {
        mode: mode,
        doc_detail: docDetail,
        file_name: fileName,
        id: response.data.data.id,
        name: docName,
        orders: 0,
        path: response.data.data.path,
        request_display: [],
        request_id: null,
        dimensions: response.data.data.dimensions,
      };
      envelopeFormData.sign_documents.push(obj);
      props.setFormObj(envelopeFormData);
      props.setDirectMakeFillableOpen(true);
      props.setSignatureDocumentData(obj);
      toast.success(response.data.message);
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    } finally {
      props.setLoading(false);
    }
  }

  const handleModeChange = (val) => {
    setMode(val);
  };

  const handleSelectSiteFiles = (selectedFiles) => {
    // Only allow one file to be selected
    if (selectedFiles.length > 1) {
      toast.error("Only one file can be selected at a time.");
      return;
    }

    const file = selectedFiles[0];
    if (!file) {
      toast.error("No file selected.");
      return;
    }

    // Validate file type and size
    const isPdf = file.file_name.toLowerCase().endsWith(".pdf");
    const sizeInKb = file.size ? parseFloat(file.size.split(' ')[0]) * (file.size.includes('MB') ? 1000 : 1) : 0;
    const isWithinSizeLimit = sizeInKb <= 8 * 1024;

    if (!isPdf) {
      toast.error(`File ${file.file_name} is not a PDF`);
      return;
    }

    if (!isWithinSizeLimit) {
      toast.error(`File ${file.file_name} exceeds 8MB limit`);
      return;
    }

    // Replace documentsList with the new file
    const newFile = {
      id: 0,
      name: file.file_name,
      kb: sizeInKb,
      file: null,
      path: file.file_path,
    };

    setDocumentsList([newFile]);
    setSiteFileLinks([file.file_path]);
    setIsSiteFilesModalOpen(false);
  };

  const handleLocalSiteFilesClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSiteFilesModalOpen(true);
  };

  return (
    <div className="offcanvas offcanvas-end modern-upload-panel" data-bs-scroll="false" tabIndex={-1}
      data-bs-keyboard="true" data-bs-backdrop="static" id="pdfUploadForm" aria-labelledby="pdfUploadFormLabel">
      <div className="offcanvas-header modern-header">
        <h5 className="offcanvas-title modern-title" id="MakeFormLabel">
          <i className="bi bi-file-earmark-pdf me-2 text-primary"></i>
          Upload PDF Document
        </h5>
        <button
          type="button"
          ref={clsFormButtonRef}
          className="btn btn-close modern-close-btn"
          data-bs-dismiss="offcanvas"
          onClick={clearForm}
          aria-label="Close"
        >
          <i className="bi bi-x-circle" aria-hidden="true" />
        </button>
      </div>
      <div className="offcanvas-body modern-body p-0" style={{ overflowX: "hidden" }}>
        <div className="modern-container">
          <div className="modern-signature-mode-section">
            <div className="modern-mode-grid">
              <div
                className={`modern-mode-card ${mode === 1 ? 'selected' : ''}`}
                data-toggle="tooltip"
                data-placement="top"
                data-bs-original-title="Request each recipient to sign their own document individually."
                onClick={() => handleModeChange(1)}
              >
                <div className="modern-mode-radio">
                  <input
                    ref={singleMode}
                    onChange={(e) => handleModeChange(parseInt(e.target.value))}
                    className="form-check-input modern-radio"
                    type="radio"
                    name="mode"
                    id="mode1"
                    value="1"
                    defaultChecked
                    checked={mode === 1}
                  />
                </div>
                <div className="modern-mode-content">
                  <i className="bi bi-person-check-fill modern-mode-icon"></i>
                  <p className="modern-mode-text">
                    Sign each recipient individually in their own document
                  </p>
                </div>
              </div>
              <div
                className={`modern-mode-card ${mode === 2 ? 'selected' : ''} ${props.formObj?.recipient_List?.length <= 1 ? 'disabled' : ''}`}
                data-toggle="tooltip"
                data-placement="top "
                data-bs-original-title={props.formObj?.recipient_List?.length <= 1 ? "Multiple recipients are required for this mode" : "Request signatures from multiple recipients on the same document."}
                onClick={() => {
                  if (props.formObj?.recipient_List?.length > 1) {
                    handleModeChange(2);
                  }
                }}
              >
                <div className="modern-mode-radio">
                  <input
                    onChange={(e) => handleModeChange(parseInt(e.target.value))}
                    className="form-check-input modern-radio"
                    type="radio"
                    name="mode"
                    id="mode2"
                    value="2"
                    checked={mode === 2}
                    disabled={props.formObj?.recipient_List?.length <= 1}
                  />
                </div>
                <div className="modern-mode-content">
                  <i className="bi bi-people-fill modern-mode-icon"></i>
                  <p className="modern-mode-text">
                    Sign multiple recipients together in same document
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="modern-document-details-card">
            <div className="modern-card-header">
              <label htmlFor="document_name" className="modern-label">
                <i className="bi bi-file-text me-2"></i>
                Document Details
              </label>
            </div>
            <div className="modern-form-group">
              <div className="modern-input-wrapper">
                <input
                  type="text"
                  className={`modern-input ${errors.doc_name ? "modern-input-error" : ""}`}
                  id="document_name"
                  value={docName}
                  onChange={(e) => setDocName(e.target.value)}
                  aria-describedby="emailHelp"
                  placeholder="Enter Signing Document Name *"
                />
                <i
                  className="bi bi-info-circle-fill modern-info-icon"
                  aria-hidden="true"
                  data-toggle="tooltip"
                  data-placement="right"
                  aria-label="Enter a name for the document that requires a digital signature."
                  data-bs-original-title="Enter a name for the document that requires a digital signature."
                />
              </div>
              {errors.doc_name && (
                <div className="modern-error-text">{errors.doc_name}</div>
              )}
            </div>
            <div className="modern-form-group">
              <div className="modern-input-wrapper">
                <textarea
                  className={`modern-textarea ${errors.doc_detail ? "modern-input-error" : ""}`}
                  id="document_detail"
                  rows="3"
                  value={docDetail}
                  onChange={(e) => setDocDetail(e.target.value)}
                  placeholder="Enter Document Description"
                />
                <i
                  className="bi bi-info-circle-fill modern-info-icon"
                  aria-hidden="true"
                  data-toggle="tooltip"
                  data-placement="right"
                  aria-label="Provide a brief description of the document that requires a digital signature." data-bs-original-title="Provide a brief description of the document that requires a digital signature."
                />
              </div>
              {errors.doc_detail && (
                <div className="modern-error-text">{errors.doc_detail}</div>
              )}
            </div>
          </div>

          <div className="modern-upload-section">
            <div className="modern-upload-header">
              <label className="modern-label">
                <i className="bi bi-cloud-upload me-2"></i>
                Upload PDF Document *
              </label>
              <i className="bi bi-info-circle-fill ms-2" aria-hidden="true" data-toggle="tooltip" data-placement="right" aria-label="Upload the PDF document that needs to be signed digitally. This will be the document recipients will review and sign." data-bs-original-title={isMyFilesEnabled ? "Choose to upload from your computer or select from files already stored on docusafe my files." : "Choose to upload from your computer."} />
            </div>
            <div className="modern-upload-container">
              <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} id="file-upload-tabs" className="drb-custom-tabs gap-4">
                <Tab eventKey="system-files" title={<><i className="bi bi-cloud-arrow-up me-2 border-right border-2" />System Files</>}>
                  <div className="modern-file-list">
                    {documentsList.length > 0 ? (
                      documentsList.map((item, index) => (
                        <div key={index} className="modern-file-item">
                          <div className="modern-file-info">
                            <i className="bi bi-file-earmark-pdf modern-file-icon"></i>
                            <span className="modern-file-name">{item.name}</span>
                          </div>
                          <button
                            onClick={(event) => onRemoveDoc(event, item)}
                            className="modern-remove-btn"
                            title="Remove file"
                          >
                            <i className="bi bi-x-circle-fill" aria-hidden="true" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="modern-no-files">
                        <i className="bi bi-inbox text-muted"></i>
                        <span className="text-muted">No files selected</span>
                      </div>
                    )}
                  </div>
                  <div
                    className={`modern-drag-area ${isDragging ? 'modern-dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="modern-upload-icon">
                      <i className="bi bi-cloud-arrow-up" aria-hidden="true" />
                    </div>
                    <h5 className="modern-upload-title">Upload your PDF file</h5>
                    <p className="modern-upload-description">
                      Drag & drop a PDF file here, or click to select a file from your computer.
                    </p>
                    <div className="modern-upload-hint">
                      <i className="bi bi-file-earmark-pdf me-1"></i>
                      Maximum file size: 8MB
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      key={fileKey}
                      id="document_file"
                      onChange={onDocFile}
                      accept="application/pdf"
                      className="d-none"
                    />
                  </div>
                </Tab>
                {isMyFilesEnabled && (
                  <Tab eventKey="local-site-files" title={<><i className="bi bi-folder me-2" />Docusafe My Files</>}>
                    <div className="modern-file-list">
                      {documentsList.length > 0 ? (
                        documentsList.map((item, index) => (
                          <div key={index} className="modern-file-item">
                            <div className="modern-file-info">
                              <i className="bi bi-file-earmark-pdf modern-file-icon"></i>
                              <span className="modern-file-name">{item.name}</span>
                            </div>
                            <button
                              onClick={(event) => onRemoveDoc(event, item)}
                              className="modern-remove-btn"
                              title="Remove file"
                            >
                              <i className="bi bi-x-circle-fill" aria-hidden="true" />
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="modern-no-files">
                          <i className="bi bi-inbox text-muted"></i>
                          <span className="text-muted">No files selected</span>
                        </div>
                      )}
                    </div>
                    <div
                      className={`modern-drag-area ${isDragging ? 'modern-dragging' : ''}`}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={handleLocalSiteFilesClick}
                    >
                      <div className="modern-upload-icon">
                        <i className="bi bi-folder" aria-hidden="true" />
                      </div>
                      <h5 className="modern-upload-title">Select from Docusafe My Files</h5>
                      <p className="modern-upload-description">
                        Click to choose a PDF file already uploaded to your site.
                      </p>
                      <div className="modern-upload-hint">
                        <i className="bi bi-folder2-open me-1"></i>
                        Browse your uploaded files
                      </div>
                    </div>
                  </Tab>
                )}
              </Tabs>
              {errors.doc_file && <div className="modern-error-text">{errors.doc_file}</div>}

            </div>
          </div>
        </div>

        <div className="modern-footer">
          <button
            type="button"
            ref={clsFormButtonRef}
            data-bs-dismiss="offcanvas"
            onClick={clearDocumentForm}
            className="btn btn-back-outline"
          >
            {/* <i className="bi bi-x-circle me-2"></i> */}
            Cancel
          </button>
          <button
            type="button"
            onClick={onSaveDoc}
            className="btn btn-save"
            disabled={props.loading}
          >
            Save
          </button>
        </div>
      </div>

      {isSiteFilesModalOpen && (
        <SiteFilesModal
          show={isSiteFilesModalOpen}
          handleClose={() => setIsSiteFilesModalOpen(false)}
          onSelectFiles={handleSelectSiteFiles}
        />
      )}
    </div>
  );
}

export default PdfUploadPopup;